import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, ButtonBase, CircularProgress, Stack, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import GridViewIcon from '@mui/icons-material/GridView';
import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { useOnboarding } from '../../../contexts/onboarding.context';
import { mixpanelEvents, onboardingConnectModalStates, PLATFORMS, platformsOnboardingIntegration, platformsThatProvideStoreUrl } from '../../../utils/constants';
import ecommercePlatforms from '../../../utils/ecommercePlatforms';
import { mobilizeiUsersService } from '../../../services/mobilizei/users.service';
import { showApiError } from '../../../utils/errors';
import { isValidUrl, normalizeUrl } from '../../../utils/url';
import OnbFrame from '../_shared/OnbFrame';
import { MbTextField } from '../../../components/MbTextFields';
import PlatformTile from '../_shared/PlatformTile';
import AuthorizationModal from './AuthorizationModal';
import useCreateStore, { buildAuthorizedStorePayload, hasPlatformAuthorization } from '../../../hooks/createStore.hook';
import { COLORS } from '../_shared/tokens';
import mixpanel from 'mixpanel-browser';

// The handoff highlights these as the main platforms; the rest come from the
// panel's own catalogue (utils/ecommercePlatforms) when the grid is expanded.
const MAIN_PLATFORM_IDS = ['nuvemshop', 'tray', 'bagy', 'irroba', 'lojaintegrada'];

const requiresStoreUrl = (platformId) => !!platformId && !platformsThatProvideStoreUrl.includes(platformId);

function SpecialTile({ Icon, label, sub, selected, dashed, onClick }) {
    return (
        <ButtonBase
            type="button"
            onClick={onClick}
            sx={{
                width: '100%',
                minWidth: 0,
                height: 124,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                px: 1.5,
                py: 2.5,
                backgroundColor: '#fff',
                border: selected
                    ? `2px solid ${COLORS.green}`
                    : dashed
                        ? '2px dashed rgba(0,0,0,0.25)'
                        : '2px solid rgba(0,0,0,0.12)',
                borderRadius: '10px',
                position: 'relative'
            }}
        >
            <Icon sx={{ fontSize: 30, color: selected ? COLORS.green : COLORS.textMuted }} />
            <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.15, textAlign: 'center' }}>{label}</Typography>
            {sub && <Typography sx={{ fontSize: 12, color: COLORS.textMuted, textAlign: 'center' }}>{sub}</Typography>}
            {selected && <CheckCircleOutlineIcon sx={{ position: 'absolute', top: 8, right: 8, fontSize: 18, color: COLORS.green }} />}
        </ButtonBase>
    );
}

/**
 * D0c · Conectar loja (step 2 of 7). States: default (main platforms),
 * expanded (all), other (manual entry), plus the Nuvemshop OAuth overlay.
 */
export default function Connect() {
    const { goToNextStep, screenState, setScreenState, data, setData } = useOnboarding();
    const { createStore } = useCreateStore();
    const { control, handleSubmit, setValue, watch, formState: { errors } } = useForm({
        defaultValues: {
            ecommercePlatformId: data.ecommercePlatformId || '',
            ecommercePlatformName: data.ecommercePlatformId === PLATFORMS.OTHER ? data.ecommercePlatformName : '',
            storeUrl: data.storeUrl || ''
        }
    });

    const [isLoading, setIsLoading] = useState(false);
    const isCreatingStore = useRef(false);

    const selectedId = watch('ecommercePlatformId');
    const showStoreUrl = requiresStoreUrl(selectedId);

    const isExpanded = ['expanded', 'other'].includes(screenState);
    const isOther = screenState === 'other';
    const isModalOpen = onboardingConnectModalStates.includes(screenState);
    const isRedirecting = screenState === 'openModalRedirecting';

    const allPlatforms = ecommercePlatforms.getAll();
    const mainPlatforms = MAIN_PLATFORM_IDS
        .map((id) => allPlatforms.find((p) => p.id === id) || { id, name: id })
        .filter(Boolean);
    const morePlatforms = allPlatforms.filter((p) => !MAIN_PLATFORM_IDS.includes(p.id) && p.id !== PLATFORMS.OTHER);

    const handleSelectPlatform = (platform, onChange) => {
        onChange(platform.id);
        if (isOther) setScreenState('expanded');
    };

    const handleSelectOther = (onChange) => {
        onChange(PLATFORMS.OTHER);
        setScreenState('other');
    };

    // Creates the store and moves on
    const createStoreAndAdvance = (payload) => {
        return createStore(payload)
            .then(() => goToNextStep())
            .catch(e => {

                showApiError(e?.error);
                setScreenState('default');
            })
            .finally(() => setIsLoading(false));
    }

    // Coming back from a platform authorization started on this step: AppAuthentication
    // drops what the platform sent on the onboarding data, and the store is created
    // with it. The data arrives after this component mounts, hence the dependencies.
    // The merchant who authorized on the platform's own admin has the store created
    // on the welcome step instead, and does not go through this one.
    useEffect(() => {
        if (!hasPlatformAuthorization(data) || isCreatingStore.current) return;

        isCreatingStore.current = true;
        setIsLoading(true);
        setScreenState('connecting');

        // Clears the platform data from the url so a refresh does not try again
        window.history.replaceState({}, '', window.location.pathname);

        createStoreAndAdvance(buildAuthorizedStorePayload(data));
    }, [data.ecommercePlatformToken, data.storeApiUsername]);

    const onSubmit = async (values) => {
        setIsLoading(true);

        try {
            const platform = ecommercePlatforms.getById(values.ecommercePlatformId);
            const isOtherPlatform = values.ecommercePlatformId === PLATFORMS.OTHER;
            const needsStoreUrl = requiresStoreUrl(values.ecommercePlatformId);

            // Left empty when the platform gives the url on the authorization callback
            const storeUrl = (needsStoreUrl && normalizeUrl(values.storeUrl)) || '';

            // Checks if the url can be reached and, on Tray/Bagy stores, gets the store id
            const checked = needsStoreUrl
                ? await mobilizeiUsersService.checkStoreUrl(values.ecommercePlatformId, storeUrl)
                : null;

            const payload = {
                ...data,
                ecommercePlatformId: values.ecommercePlatformId,
                ecommercePlatformName: isOtherPlatform ? values.ecommercePlatformName : platform.name,
                ecommercePlatformAllowsIframe: platform.isIframeAllowed,
                storeId: checked?.storeId || data.storeId,
                storeUrl,
                storeAccountUrl: storeUrl && storeUrl + (platform.storeAccountUrl || ''),
                storeCartUrl: storeUrl && storeUrl + (platform.storeCartUrl || '')
            }

            setData(payload);

            mixpanel.track(mixpanelEvents.ONBOARDING_ECOMMERCE_PLATFORM_SELECTED, {
                'platform_id': platform.id,
                'store_url': storeUrl,
            })

            // These platforms require an authorization before the store can be created
            if (platformsOnboardingIntegration.includes(values.ecommercePlatformId)) {
                setIsLoading(false);
                setScreenState('openModalAuthorization');
                return;
            }

            await createStoreAndAdvance(payload);
        } catch (e) {
            showApiError(e.error);
            setIsLoading(false);
        }
    };

    const handleRedirecting = () => setScreenState('openModalRedirecting');

    return (
        <OnbFrame step={2}>
            <Box
                component="form"
                onSubmit={handleSubmit(onSubmit)}
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: { xs: 'flex-start', md: 'center' },
                }}
            >
                <Box sx={{ maxWidth: 720, mb: 3 }}>
                    <Typography
                        component="h1"
                        sx={{ fontWeight: 700, fontSize: { xs: 26, md: 36 }, lineHeight: 1.1, letterSpacing: '-0.5px', mb: 1.5 }}
                    >
                        Qual é a sua plataforma de e-commerce?
                    </Typography>
                    <Typography sx={{ fontSize: { xs: 16, md: 17 }, lineHeight: 1.5, color: COLORS.textMuted, textAlign: 'center' }}>
                        Vamos conectar sua loja e trazer seus produtos automaticamente para o aplicativo.
                    </Typography>
                </Box>

                <Box sx={{ maxWidth: 720, width: '100%' }}>
                    <Controller
                        name="ecommercePlatformId"
                        control={control}
                        rules={{ required: "Selecione a plataforma da sua loja." }}
                        render={({ field }) => (
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.75 }}>
                                {mainPlatforms.map((platform) => (
                                    <PlatformTile
                                        key={platform.id}
                                        platform={platform}
                                        selected={!isOther && field.value === platform.id}
                                        onClick={() => handleSelectPlatform(platform, field.onChange)}
                                    />
                                ))}

                                {!isExpanded && (
                                    <SpecialTile
                                        Icon={GridViewIcon}
                                        label="Ver todas as plataformas"
                                        sub={`+${morePlatforms.length + 1} opções`}
                                        onClick={() => setScreenState('expanded')}
                                    />
                                )}

                                {isExpanded && morePlatforms.map((platform) => (
                                    <PlatformTile
                                        key={platform.id}
                                        platform={platform}
                                        selected={!isOther && field.value === platform.id}
                                        onClick={() => handleSelectPlatform(platform, field.onChange)}
                                    />
                                ))}

                                {isExpanded && (
                                    <SpecialTile
                                        Icon={AddBusinessOutlinedIcon}
                                        label="Outra"
                                        selected={isOther}
                                        dashed={!isOther}
                                        onClick={() => handleSelectOther(field.onChange)}
                                    />
                                )}
                            </Box>
                        )}
                    />

                    {errors.ecommercePlatformId && (
                        <Typography sx={{ fontSize: 14, lineHeight: 1.4, mt: 1.25, color: COLORS.errorFg }}>
                            {errors.ecommercePlatformId.message}
                        </Typography>
                    )}
                </Box>

                {showStoreUrl && (
                    <Box sx={{ maxWidth: 560, width: '100%', mt: 3.25 }}>
                        {isOther && (
                            <Stack mb={2}>
                                <Controller
                                    name="ecommercePlatformName"
                                    control={control}
                                    shouldUnregister
                                    rules={{ required: "O nome da sua plataforma é obrigatório." }}
                                    render={({ field }) => (
                                        <MbTextField
                                            label="Qual plataforma você usa?"
                                            value={field.value}
                                            onChange={field.onChange}
                                            error={errors.ecommercePlatformName?.message}
                                        />
                                    )}
                                />
                            </Stack>
                        )}

                        <Controller
                            name="storeUrl"
                            control={control}
                            // Unregisters with the field so the platforms that do not
                            // show it are not held back by its validation.
                            shouldUnregister
                            rules={{
                                required: "A URL da sua loja é obrigatória.",
                                validate: (value) => isValidUrl(value) || "Parece que tem algo de errado com esta url! Verifique se ela coincide com a URL da sua loja."
                            }}
                            // Mobile keyboards capitalize the first letter and autocorrect
                            // what is typed by default, neither of which a url wants.
                            render={({ field }) => (
                                <MbTextField
                                    label="URL da sua loja"
                                    placeholder="Ex.: https://www.sualoja.com.br"
                                    // The error message takes the helper's place, so both
                                    // are not stacked under the field.
                                    helper={!errors.storeUrl && "Insira o endereço que seus clientes usam para acessar sua loja."}
                                    inputMode="url"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    value={field.value}
                                    onChange={field.onChange}
                                    // Shows the standard form of what was typed
                                    // (loja.com.br -> https://loja.com.br), so the saved
                                    // url is never a surprise.
                                    onBlur={() => {
                                        field.onBlur();
                                        const normalized = normalizeUrl(field.value);
                                        if (normalized) setValue('storeUrl', normalized, { shouldValidate: true });
                                    }}
                                    error={errors.storeUrl?.message}
                                />
                            )}
                        />
                    </Box>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'stretch', md: 'center' },
                        gap: { xs: 2, md: 2.5 },
                        mt: 3.25
                    }}
                >
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isLoading}
                        endIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />}
                        sx={{ py: 2, fontSize: 16, flex: 'none', boxShadow: '0 10px 28px rgba(60,213,104,0.4)' }}
                    >
                        {isLoading ? 'conectando...' : 'conectar minha loja'}
                    </Button>

                    <Typography sx={{ fontSize: 14, lineHeight: 1.4, color: COLORS.textMuted, display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                        <ShieldOutlinedIcon sx={{ fontSize: 16 }} />
                        não vamos alterar nada na sua loja
                    </Typography>
                </Box>
            </Box>

            <AuthorizationModal
                open={isModalOpen}
                platformId={selectedId}
                redirecting={isRedirecting}
                storeUrl={watch('storeUrl')}
                onRedirecting={handleRedirecting}
                onClose={() => setScreenState('default')}
            />
        </OnbFrame>
    );
}
